import { useEffect, useRef, type MutableRefObject } from "react";
import * as THREE from "three";

const tunnelVertex = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const tunnelFragment = `
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uProgress;
  uniform float uVelocity;
  uniform vec2 uPointer;
  uniform vec2 uResolution;

  float hash(vec2 p) {
    p = fract(p * vec2(443.897, 441.423));
    p += dot(p, p.yx + 19.19);
    return fract(p.x * p.y);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1., 0.)), f.x), mix(hash(i + vec2(0., 1.)), hash(i + 1.), f.x), f.y);
  }

  void main() {
    vec2 p = (vUv - .5) * vec2(uResolution.x / max(uResolution.y, 1.), 1.);
    p += uPointer * vec2(.032, .024) * (1.0 - smoothstep(.2, 1.1, length(p)));
    float r = length(p);
    float a = atan(p.y, p.x);
    float velocity = min(abs(uVelocity), 1.0);
    float direction = uVelocity < 0.0 ? -1.0 : 1.0;
    float travel = uProgress * 24.0;
    float twist = a + sin(r * 7.0 - travel * .42) * .34 + travel * .18;
    twist += sin(a * 3.0 + travel + r * 14.0) * velocity * .14 * direction;
    float depth = 1.0 / max(r, .035) + travel;
    float ribs = sin(depth * 5.2 + twist * 8.0);
    float lace = sin(depth * 2.4 - twist * 17.0 + noise(vec2(twist * 2., depth * .08)) * 4.0);
    float cells = smoothstep(.74, 1.0, sin(twist * 13. + depth * 1.7) * sin(twist * 7. - depth * 2.1));
    float pulse = .5 + .5 * sin(depth * .55 - uTime * .45);
    float speedRays = pow(max(0.0, sin(a * 26.0 - depth * .28 * direction + uTime * .6)), 10.0) * velocity;

    vec3 ink = vec3(.003, .005, .026);
    vec3 cobalt = vec3(.015, .08, 1.0);
    vec3 violet = vec3(.48, .0, 1.0);
    vec3 cyan = vec3(.0, .92, 1.0);
    vec3 magenta = vec3(1.0, .02, .55);
    vec3 acid = vec3(.87, 1.0, .0);
    vec3 coral = vec3(1.0, .12, .015);
    vec3 bone = vec3(.96, .93, .84);

    vec3 tunnel = mix(cobalt, violet, .5 + .5 * sin(twist * 2.4 + depth * .18));
    tunnel = mix(tunnel, cyan, smoothstep(.55, 1., ribs) * .66);
    tunnel = mix(tunnel, magenta, smoothstep(.4, 1., lace) * .36);
    tunnel += cells * vec3(.38, .05, .85) * (1.0 - smoothstep(.1, 1.2, r));
    tunnel *= .18 + .92 * pow(abs(ribs * lace), .62);
    tunnel *= .66 + pulse * .42;

    float petals = 1.0 - smoothstep(.0, .24, abs(sin(a * 7.0 + sin(depth * .18) * 2.8 + travel * .14)));
    float bloomRings = 1.0 - smoothstep(.0, .18, abs(sin(depth * 1.75 - uTime * .22)));
    float pollen = cells * (1.0 - smoothstep(.16, .92, r));
    vec3 bloom = mix(coral, magenta, .5 + .5 * sin(a * 3.0 + depth * .11));
    bloom = mix(bloom, acid, bloomRings * .82);
    bloom = mix(ink, bloom, .12 + petals * 1.05 + bloomRings * .58 + pollen * .28);

    float spokes = 1.0 - smoothstep(.0, .13, abs(sin(a * 12.0 + depth * .035)));
    float plans = 1.0 - smoothstep(.0, .16, abs(sin(depth * 2.7 + floor(a * 6.0) * .7)));
    float blocks = step(.84, noise(vec2(floor(a * 16.0), floor(depth * .22))));
    vec3 blueprint = ink;
    blueprint += spokes * mix(cyan, bone, plans) * 1.25;
    blueprint += plans * cobalt * 1.05;
    blueprint += blocks * acid * .5 * (1.0 - smoothstep(.12, 1.0, r));

    float braid = 1.0 - smoothstep(.0, .2, abs(sin(depth * 3.1 + a * 9.0 + sin(depth * .08) * 4.0)));
    float halo = 1.0 - smoothstep(.0, .12, abs(sin(depth * .88 - uTime * .18)));
    vec3 aperture = mix(violet, cyan, .5 + .5 * sin(a * 4.0 + travel));
    aperture = mix(aperture, bone, halo);
    aperture *= .12 + braid * .95 + halo * .75;

    vec3 color = mix(tunnel, bloom, smoothstep(.18, .34, uProgress));
    color = mix(color, blueprint, smoothstep(.43, .59, uProgress));
    color = mix(color, aperture, smoothstep(.70, .86, uProgress));
    color += speedRays * mix(cyan, magenta, .5 + .5 * sin(a * 5.0 + travel)) * (1.15 - smoothstep(.42, 1.2, r));

    float voidCore = 1.0 - smoothstep(.035, .19 + .04 * sin(travel), r);
    color = mix(color, ink, voidCore * (1.0 - smoothstep(.76, .94, uProgress)));
    float exitCore = (1.0 - smoothstep(.01, mix(.04, .82, smoothstep(.84, 1.0, uProgress)), r)) * smoothstep(.82, 1.0, uProgress);
    color = mix(color, vec3(1.0), exitCore);
    color *= 1.0 - smoothstep(.62, 1.1, r);
    float grain = hash(gl_FragCoord.xy + floor(uTime * 18.)) - .5;
    color += grain * .035;
    gl_FragColor = vec4(pow(max(color, 0.0), vec3(.82)), 1.0);
  }
`;

function ribbonGeometry(turns = 5, points = 180) {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let index = 0; index <= points; index += 1) {
    const t = index / points;
    const angle = t * Math.PI * 2 * turns;
    const radius = 1.25 + Math.sin(t * Math.PI * 9) * 0.22;
    const center = new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, (t - 0.5) * 3.4);
    const tangent = new THREE.Vector3(-Math.sin(angle), Math.cos(angle), 0.28).normalize();
    const side = new THREE.Vector3(0, 0, 1).cross(tangent).normalize().multiplyScalar(0.18 + Math.sin(t * 13) * 0.07);
    positions.push(center.x + side.x, center.y + side.y, center.z + side.z);
    positions.push(center.x - side.x, center.y - side.y, center.z - side.z);
    if (index < points) {
      const base = index * 2;
      indices.push(base, base + 1, base + 2, base + 1, base + 3, base + 2);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function mobiusGeometry(segments = 180, widthSegments = 12) {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let segment = 0; segment <= segments; segment += 1) {
    const u = (segment / segments) * Math.PI * 2;
    for (let across = 0; across <= widthSegments; across += 1) {
      const v = (across / widthSegments - 0.5) * 0.92;
      const reach = 1.35 + v * Math.cos(u * 0.5);
      positions.push(reach * Math.cos(u), reach * Math.sin(u), v * Math.sin(u * 0.5));
      if (segment >= segments || across >= widthSegments) continue;
      const row = widthSegments + 1;
      const base = segment * row + across;
      indices.push(base, base + row, base + 1, base + 1, base + row, base + row + 1);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function scribbleGeometry() {
  const points = Array.from({ length: 28 }, (_, index) => {
    const angle = (index / 28) * Math.PI * 2;
    const radius = 1.12 + Math.sin(angle * 5) * 0.38 + Math.cos(angle * 9) * 0.12;
    return new THREE.Vector3(
      Math.cos(angle) * radius,
      Math.sin(angle) * radius,
      Math.sin(angle * 3) * 0.48,
    );
  });
  const curve = new THREE.CatmullRomCurve3(points, true, "centripetal", 0.36);
  return new THREE.TubeGeometry(curve, 190, 0.055, 6, true);
}

type PortalTunnelProps = {
  progressRef: MutableRefObject<number>;
  velocityRef: MutableRefObject<number>;
};

export function PortalTunnel({ progressRef, velocityRef }: PortalTunnelProps) {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(58, 1, 0.1, 120);
    camera.position.set(0, 0, 8);
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: "high-performance" });
    } catch {
      mount.classList.add("is-fallback");
      return () => mount.classList.remove("is-fallback");
    }
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.25;
    mount.appendChild(renderer.domElement);

    const uniforms = {
      uTime: { value: 0 },
      uProgress: { value: reduced ? 0.72 : 0 },
      uVelocity: { value: 0 },
      uPointer: { value: new THREE.Vector2() },
      uResolution: { value: new THREE.Vector2(1, 1) },
    };
    const backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({ vertexShader: tunnelVertex, fragmentShader: tunnelFragment, uniforms, depthWrite: false, depthTest: false }),
    );
    backdrop.frustumCulled = false;
    backdrop.renderOrder = -10;
    scene.add(backdrop);

    const artifactGroup = new THREE.Group();
    scene.add(artifactGroup);
    const cyan = new THREE.MeshBasicMaterial({ color: 0x00eaff, wireframe: true, transparent: true, opacity: 0.76 });
    const violet = new THREE.MeshBasicMaterial({ color: 0x8d2cff, wireframe: true, transparent: true, opacity: 0.86 });
    const pink = new THREE.MeshBasicMaterial({ color: 0xff158c, side: THREE.DoubleSide, transparent: true, opacity: 0.72 });
    const acid = new THREE.MeshBasicMaterial({ color: 0xdfff00, wireframe: true, transparent: true, opacity: 0.74 });
    const orange = new THREE.MeshBasicMaterial({ color: 0xff4d19, side: THREE.DoubleSide, transparent: true, opacity: 0.82 });
    const bone = new THREE.MeshBasicMaterial({ color: 0xf4efe1, wireframe: true, transparent: true, opacity: 0.88 });

    const artifacts: THREE.Object3D[] = [];
    const knot = new THREE.Mesh(new THREE.TorusKnotGeometry(1.25, 0.18, 160, 12, 2, 5), cyan);
    knot.position.set(-1.9, 0.5, -8); artifactGroup.add(knot); artifacts.push(knot);
    const scribble = new THREE.Mesh(scribbleGeometry(), orange);
    scribble.scale.set(1.15, 0.8, 1.2); scribble.position.set(1.55, -0.35, -15); artifactGroup.add(scribble); artifacts.push(scribble);
    const cage = new THREE.Mesh(new THREE.IcosahedronGeometry(1.55, 2), violet);
    cage.scale.set(1, 1.55, 0.72); cage.position.set(-2.1, -0.6, -23); artifactGroup.add(cage); artifacts.push(cage);
    const mobius = new THREE.Mesh(mobiusGeometry(), orange);
    mobius.rotation.set(0.55, -0.28, 0.22); mobius.position.set(1.35, 0.2, -31); artifactGroup.add(mobius); artifacts.push(mobius);
    const ribbon = new THREE.Mesh(ribbonGeometry(), pink);
    ribbon.rotation.set(0.6, 0.3, 0.1); ribbon.position.set(-1.3, 0.25, -40); artifactGroup.add(ribbon); artifacts.push(ribbon);

    const gyroscope = new THREE.Group();
    [1.0, 1.45, 1.9].forEach((radius, index) => {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(radius, 0.045 + index * 0.012, 8, 96), index === 1 ? acid : violet);
      ring.rotation.set(index * 0.8, index * 0.48, index * 0.64);
      gyroscope.add(ring);
    });
    gyroscope.position.set(1.1, 0, -51); artifactGroup.add(gyroscope); artifacts.push(gyroscope);

    const blackSun = new THREE.Mesh(new THREE.DodecahedronGeometry(1.4, 1), bone);
    blackSun.scale.set(1.35, 1.35, 0.5); blackSun.position.set(-0.65, 0.1, -59); artifactGroup.add(blackSun); artifacts.push(blackSun);

    const shardCount = 64;
    const shards = new THREE.InstancedMesh(new THREE.TetrahedronGeometry(0.13, 0), acid, shardCount);
    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    for (let index = 0; index < shardCount; index += 1) {
      const z = -4 - (index / (shardCount - 1)) * 54;
      const angle = index * 2.39996;
      const radius = 2.7 + Math.sin(index * 1.7) * 0.72;
      quaternion.setFromEuler(new THREE.Euler(index * 0.17, index * 0.31, angle));
      scale.setScalar(0.55 + (index % 7) * 0.12);
      matrix.compose(new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, z), quaternion, scale);
      shards.setMatrixAt(index, matrix);
    }
    shards.instanceMatrix.needsUpdate = true;
    artifactGroup.add(shards);

    const helixCount = 96;
    const helixMaterial = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.92 });
    const helix = new THREE.InstancedMesh(new THREE.BoxGeometry(0.1, 0.34, 0.08), helixMaterial, helixCount);
    const helixColors = [new THREE.Color(0x00eaff), new THREE.Color(0xff158c), new THREE.Color(0xdfff00), new THREE.Color(0xff4d19)];
    for (let index = 0; index < helixCount; index += 1) {
      const strand = index % 2;
      const step = Math.floor(index / 2);
      const angle = step * 0.53 + strand * Math.PI;
      const z = -5 - step * 1.22;
      const radius = 2.18 + Math.sin(step * 0.7) * 0.18;
      quaternion.setFromEuler(new THREE.Euler(0, 0, angle + Math.PI * 0.5));
      scale.set(1, 0.7 + (step % 5) * 0.1, 1);
      matrix.compose(new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, z), quaternion, scale);
      helix.setMatrixAt(index, matrix);
      const helixColor = helixColors[(step + strand) % helixColors.length];
      if (helixColor) helix.setColorAt(index, helixColor);
    }
    helix.instanceMatrix.needsUpdate = true;
    if (helix.instanceColor) helix.instanceColor.needsUpdate = true;
    artifactGroup.add(helix);

    const gateCount = 14;
    const gateMaterial = new THREE.MeshBasicMaterial({ color: 0xf4efe1, side: THREE.DoubleSide, transparent: true, opacity: 0.54 });
    const gates = new THREE.InstancedMesh(new THREE.RingGeometry(2.5, 2.57, 4, 1), gateMaterial, gateCount);
    for (let index = 0; index < gateCount; index += 1) {
      const z = -7 - index * 4.15;
      const gateScale = 0.82 + Math.sin(index * 1.81) * 0.11;
      quaternion.setFromEuler(new THREE.Euler(0, 0, index * 0.31));
      scale.set(gateScale, gateScale, gateScale);
      matrix.compose(new THREE.Vector3(0, 0, z), quaternion, scale);
      gates.setMatrixAt(index, matrix);
    }
    gates.instanceMatrix.needsUpdate = true;
    artifactGroup.add(gates);

    let frame = 0;
    let visible = !document.hidden;
    let inView = false;
    const timer = new THREE.Timer();
    const pointerTarget = new THREE.Vector2();
    const pointerCurrent = new THREE.Vector2();
    const onPointerMove = (event: PointerEvent) => {
      if (reduced) return;
      pointerTarget.set(
        THREE.MathUtils.clamp((event.clientX / Math.max(window.innerWidth, 1)) * 2 - 1, -1, 1),
        THREE.MathUtils.clamp((event.clientY / Math.max(window.innerHeight, 1)) * -2 + 1, -1, 1),
      );
    };
    const onPointerLeave = () => pointerTarget.set(0, 0);
    const resize = () => {
      const width = Math.max(1, mount.clientWidth);
      const height = Math.max(1, mount.clientHeight);
      const pixels = width * height;
      const quality = pixels > 1_100_000 ? 0.72 : pixels > 520_000 ? 0.8 : 0.9;
      const ratio = Math.min(window.devicePixelRatio || 1, 1.15) * quality;
      if (Math.abs(renderer.getPixelRatio() - ratio) > 0.01) renderer.setPixelRatio(ratio);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
      uniforms.uResolution.value.set(renderer.domElement.width, renderer.domElement.height);
    };
    function schedule() {
      if (reduced || !visible || !inView || frame !== 0) return;
      frame = window.requestAnimationFrame(draw);
    }

    function draw(timestamp?: number) {
      frame = 0;
      timer.update(timestamp);
      const elapsed = timer.getElapsed();
      const delta = Math.min(timer.getDelta(), 0.05);
      const progress = reduced ? 0.72 : progressRef.current;
      const velocity = reduced ? 0 : THREE.MathUtils.clamp(velocityRef.current, -1, 1);
      const follow = 1 - Math.exp(-delta * 8);
      const pointerFollow = 1 - Math.exp(-delta * 5);
      uniforms.uTime.value = elapsed;
      uniforms.uProgress.value += (progress - uniforms.uProgress.value) * follow;
      uniforms.uVelocity.value += (velocity - uniforms.uVelocity.value) * follow;
      pointerCurrent.lerp(pointerTarget, pointerFollow);
      uniforms.uPointer.value.copy(pointerCurrent);
      camera.position.z = 8 - uniforms.uProgress.value * 68;
      camera.position.x = Math.sin(uniforms.uProgress.value * Math.PI * 5) * 0.16 + pointerCurrent.x * 0.2;
      camera.position.y = Math.cos(uniforms.uProgress.value * Math.PI * 4) * 0.12 + pointerCurrent.y * 0.14;
      camera.rotation.z = Math.sin(uniforms.uProgress.value * Math.PI * 3) * 0.055 + uniforms.uVelocity.value * 0.045;
      artifacts.forEach((artifact, index) => {
        artifact.rotation.x += delta * (0.07 + index * 0.019 + Math.abs(uniforms.uVelocity.value) * 0.11);
        artifact.rotation.y += delta * (0.1 + index * 0.026 + Math.abs(uniforms.uVelocity.value) * 0.16);
      });
      artifactGroup.position.x = pointerCurrent.x * -0.1;
      artifactGroup.position.y = pointerCurrent.y * -0.08;
      shards.rotation.z = elapsed * 0.035 + uniforms.uProgress.value * 0.7;
      helix.rotation.z = elapsed * 0.04 + uniforms.uProgress.value * Math.PI * 1.7;
      gates.rotation.z = elapsed * -0.018 + uniforms.uProgress.value * -0.42;
      renderer.render(scene, camera);
      schedule();
    }
    const onVisibility = () => {
      visible = !document.hidden;
      if (!visible && frame) { window.cancelAnimationFrame(frame); frame = 0; }
      else schedule();
    };
    const resizeObserver = new ResizeObserver(resize);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inView = entry?.isIntersecting ?? false;
      if (!inView && frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      } else if (inView) {
        timer.reset();
        schedule();
      }
    }, { rootMargin: "30% 0px" });
    resizeObserver.observe(mount);
    intersectionObserver.observe(mount);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onPointerLeave);
    resize(); draw();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pointermove", onPointerMove);
      document.documentElement.removeEventListener("pointerleave", onPointerLeave);
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          if (Array.isArray(object.material)) object.material.forEach((material) => material.dispose());
          else object.material.dispose();
        }
      });
      timer.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [progressRef, velocityRef]);

  return <div className="portal-tunnel" ref={mountRef} aria-hidden="true" />;
}
